/*
 * Card presentation metadata, keyed by cardType.
 *
 * TEMPORARY. The old /v1/getPhysicalCards resolved these server-side and returned them per
 * card; GET /me/cards does not, so My Cards cannot render from the API alone. Values are
 * taken verbatim from __mocks__/api/mockCardData.js, which was captured from a real
 * response (see ID-2699 in that file).
 *
 * The trade-off, stated plainly: this is business content - artwork, product names, the
 * phone numbers customers are told to call - living in the frontend. A copy change now
 * needs a link.sg deploy, and a card type we have no entry for renders without artwork or
 * a name. Delete this file and read the fields straight through the moment the API
 * supplies them.
 *
 * Known gap: the new API has been seen returning cardType 840100, which has no entry here
 * because the captured response never contained one. See cardPresentation() below for what
 * happens then.
 *
 * customerCareCall is deliberately absent. It was '62235225' on every card in the capture
 * and nothing in link.sg renders it, so carrying it would be dead data.
 */

const IMAGE_BASE = 'https://media.nedigital.sg/linkpass/images';
const MEMBERSHIP_SUBMSG = 'Membership';
const PAYMENT_CARD_SUBMSG = 'Payment card';
const REPLACE_CARD_ACTION = 'REPLACE_CARD';
const REPLACE_CARD_TEXT = 'Replace card';

const TRUST_REPLACEMENT =
  'You can replace your card via the Trust app or call Trust at +65 313 TRUST (+65 313 87878)';
const OCBC_REPLACEMENT = 'You can replace your card by calling OCBC at 1800 363 3333';

/*
 * maskNumber marks the types whose sub-message is the card number rather than a static
 * label - the capture showed "8105 **** **** 5857" against CardNumber 8105000001415857.
 *
 * 820200 has no replacementActionType, and that is not an oversight: the component treats
 * anything other than REPLACE_CARD as "show the message" and only greys it out when the
 * value is exactly PHONE, so leaving it absent reproduces the old behaviour.
 */
const CARDS = {
  810300: {
    cardName: 'nEbO card',
    cardNameSubMsg: MEMBERSHIP_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/75957183-75e0-4bdc-bcd4-bd6ab27719e9/ntuc_nebo_plus_card.png`,
    cardReplacementMessage: 'You can replace your card by sending an email to nEbOcso2@nEbO.com.sg',
    replacementActionType: 'PHONE',
  },
  810500: {
    cardName: 'Link card (formerly Plus! card)',
    maskNumber: true,
    cardFrontUrl: `${IMAGE_BASE}/1e5fa3bd-17e8-4f84-a5df-8304684083a8/plus_card.png`,
    cardReplacementMessage: REPLACE_CARD_TEXT,
    replacementActionType: REPLACE_CARD_ACTION,
  },
  810501: {
    cardName: 'Link card (formerly Plus! card)',
    maskNumber: true,
    cardFrontUrl: `${IMAGE_BASE}/1e5fa3bd-17e8-4f84-a5df-8304684083a8/plus_card.png`,
    cardReplacementMessage: REPLACE_CARD_TEXT,
    replacementActionType: REPLACE_CARD_ACTION,
  },
  810503: {
    cardName: 'NTUC Social Membership card',
    cardNameSubMsg: MEMBERSHIP_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/1e5fa3bd-17e8-4f84-a5df-8304684083a8/plus_card.png`,
    cardReplacementMessage: REPLACE_CARD_TEXT,
    replacementActionType: REPLACE_CARD_ACTION,
  },
  810800: {
    cardName: 'NTUC card',
    cardNameSubMsg: MEMBERSHIP_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/4767ce2c-522c-42d4-ad84-ab8417bc2941/ntuc_link_plus_card.png`,
    cardReplacementMessage: REPLACE_CARD_TEXT,
    replacementActionType: REPLACE_CARD_ACTION,
  },
  820100: {
    cardName: 'OCBC Plus! Visa Credit/Debit card',
    cardNameSubMsg: PAYMENT_CARD_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/d789acb0-0a4f-4dcb-848e-6a2488bc589a/plus_visa_card.png`,
    cardReplacementMessage: OCBC_REPLACEMENT,
    replacementActionType: 'PHONE',
  },
  820200: {
    cardName: 'Trust Visa Credit/Debit card',
    cardNameSubMsg: PAYMENT_CARD_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/b6b56d05-8fd3-4f35-ad1e-b32d008e446a/link_trust_visa_card.png`,
    cardReplacementMessage: TRUST_REPLACEMENT,
  },
  830100: {
    cardName: 'OCBC NTUC Visa Credit/Debit card',
    cardNameSubMsg: PAYMENT_CARD_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/0c9f9572-2a95-4ad5-b273-e061718adfe9/ntuc_plus_visa_card.png`,
    cardReplacementMessage: OCBC_REPLACEMENT,
    replacementActionType: 'PHONE',
  },
  830200: {
    cardName: 'Trust NTUC Visa Credit/Debit card',
    cardNameSubMsg: PAYMENT_CARD_SUBMSG,
    cardFrontUrl: `${IMAGE_BASE}/7c580e3b-f9fd-41a0-ab3f-d00bce0d4bce/ntuc_link_trust_visa_card.png`,
    cardReplacementMessage: TRUST_REPLACEMENT,
    replacementActionType: 'PHONE',
  },
};

// "8105000001415857" -> "8105 **** **** 5857"
export const maskedCardNumber = (n) =>
  typeof n === 'string' && n.length >= 8 ? `${n.slice(0, 4)} **** **** ${n.slice(-4)}` : '';

/*
 * Returns {} for a card type we have no entry for, so the card still lists with its number
 * and status rather than disappearing. It renders without artwork or a name - incomplete,
 * but honest, and better than captioning someone's card with a product it is not.
 */
export const cardPresentation = (cardType, cardNumber) => {
  const card = CARDS[cardType];
  if (!card) {
    return {};
  }

  return {
    cardName: card.cardName,
    cardNameSubMsg: card.maskNumber ? maskedCardNumber(cardNumber) : card.cardNameSubMsg,
    cardFrontUrl: card.cardFrontUrl,
    cardReplacementMessage: card.cardReplacementMessage,
    ...(card.replacementActionType
      ? { replacementActionType: card.replacementActionType }
      : undefined),
  };
};
